// Queue health scorecard — rows = queues, columns = volume / timing / outcome
// mix. Lets a supervisor glance at which queues are in trouble.
//
// Powered by taskLegDetails grouped by queue.name. Legs that have a non-null
// queue are the units of work the queue did; aggregation picks the right
// one (contacts for volume, averages for timing, counts for outcome mix).

import React from 'react';
import { Badge, Progress, Text, Tooltip } from '@mantine/core';
import { ReportTable } from './ReportTable';
import { fmtDuration } from '../utils';

const REPORT_ID = 'queue-health';

const COLUMNS = [
  {
    id: 'name',
    label: 'Queue',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.name || <em>(no queue)</em>}</Text>,
  },
  {
    id: 'volume',
    label: 'Legs',
    hint: 'Number of leg records routed to this queue in the window.',
    align: 'right',
    render: (r) => (
      <Tooltip label={`${r.volume} legs / ${r.uniqueContacts} unique contacts`}>
        <Text size="sm">{r.volume}</Text>
      </Tooltip>
    ),
  },
  {
    id: 'uniqueContacts',
    label: 'Unique contacts',
    hint: 'Distinct interactionIds that touched the queue.',
    align: 'right',
    render: (r) => <Text size="sm">{r.uniqueContacts}</Text>,
  },
  {
    id: 'avgHandle',
    label: 'Avg handle',
    hint: 'Mean handleTime across legs with a non-null value.',
    align: 'right',
    render: (r) => <Text size="sm">{fmtDuration(r.avgHandleMs)}</Text>,
  },
  {
    id: 'avgHold',
    label: 'Avg hold',
    hint: 'Mean holdDuration across legs.',
    align: 'right',
    render: (r) => <Text size="sm">{fmtDuration(r.avgHoldMs)}</Text>,
  },
  {
    id: 'avgRinging',
    label: 'Avg ring',
    hint: 'Mean ringingDuration — long values suggest understaffed queues.',
    align: 'right',
    render: (r) => <Text size="sm">{fmtDuration(r.avgRingingMs)}</Text>,
  },
  {
    id: 'avgWrapup',
    label: 'Avg wrap',
    hint: 'Mean wrapupDuration — long values suggest agent inefficiency or complex calls.',
    align: 'right',
    render: (r) => <Text size="sm">{fmtDuration(r.avgWrapupMs)}</Text>,
  },
  {
    id: 'abandonRate',
    label: 'Abandon',
    hint: 'Share of legs with a non-null abandonedType.',
    align: 'right',
    render: (r) => <PctBadge pct={r.abandonRate} color="orange" />,
  },
  {
    id: 'transferRate',
    label: 'Transfer-out',
    hint: 'Share of legs whose terminationReason indicates a transfer.',
    align: 'right',
    render: (r) => <PctBadge pct={r.transferRate} color="violet" />,
  },
  {
    id: 'ownerCoverage',
    label: 'Covered by',
    hint: 'Distinct owner (agent) names that handled a leg from this queue.',
    align: 'right',
    render: (r) => <Text size="sm">{r.distinctOwners}</Text>,
  },
  {
    id: 'mix',
    label: 'Mix',
    hint: 'Visual outcome split: handled vs transferred vs abandoned.',
    width: 180,
    render: (r) => (
      <Progress.Root size="md">
        {r.pctHandled > 0 && <Progress.Section value={r.pctHandled} color="teal" />}
        {r.pctTransferred > 0 && <Progress.Section value={r.pctTransferred} color="violet" />}
        {r.pctAbandoned > 0 && <Progress.Section value={r.pctAbandoned} color="orange" />}
      </Progress.Root>
    ),
  },
];

const DEFAULT_VISIBLE = ['volume', 'uniqueContacts', 'avgHandle', 'avgHold', 'abandonRate', 'transferRate', 'mix'];

export function QueueHealth({ data }) {
  const rows = buildRows(data.legs);
  return (
    <ReportTable
      title="Queue health scorecard"
      subtitle={`${rows.length} queue${rows.length === 1 ? '' : 's'} active in window · based on taskLegDetails`}
      reportId={REPORT_ID}
      columns={COLUMNS}
      defaultVisibleIds={DEFAULT_VISIBLE}
      rows={rows}
      emptyLabel="No queue activity recorded in this window."
    />
  );
}

function buildRows(legs) {
  const buckets = new Map();
  for (const l of legs) {
    const name = l?.queue?.name ?? null;
    if (!name) continue;
    if (!buckets.has(name)) {
      buckets.set(name, {
        key: name,
        name,
        volume: 0,
        contacts: new Set(),
        owners: new Set(),
        sumHandle: 0, countHandle: 0,
        sumHold: 0, countHold: 0,
        sumRinging: 0, countRinging: 0,
        sumWrapup: 0, countWrapup: 0,
        abandoned: 0,
        transferred: 0,
      });
    }
    const b = buckets.get(name);
    b.volume += 1;
    if (l.taskId) b.contacts.add(l.taskId);
    if (l.owner?.name) b.owners.add(l.owner.name);
    if (l.handleTime != null) { b.sumHandle += l.handleTime; b.countHandle += 1; }
    if (l.holdDuration != null) { b.sumHold += l.holdDuration; b.countHold += 1; }
    if (l.ringingDuration != null) { b.sumRinging += l.ringingDuration; b.countRinging += 1; }
    if (l.wrapupDuration != null) { b.sumWrapup += l.wrapupDuration; b.countWrapup += 1; }
    if (l.abandonedType) b.abandoned += 1;
    if (isTransfer(l.terminationReason)) b.transferred += 1;
  }

  return [...buckets.values()]
    .map((b) => {
      const handled = Math.max(0, b.volume - b.abandoned - b.transferred);
      return {
        key: b.key,
        name: b.name,
        volume: b.volume,
        uniqueContacts: b.contacts.size,
        distinctOwners: b.owners.size,
        avgHandleMs: safeAvg(b.sumHandle, b.countHandle),
        avgHoldMs: safeAvg(b.sumHold, b.countHold),
        avgRingingMs: safeAvg(b.sumRinging, b.countRinging),
        avgWrapupMs: safeAvg(b.sumWrapup, b.countWrapup),
        abandonRate: pct(b.abandoned, b.volume),
        transferRate: pct(b.transferred, b.volume),
        pctHandled: pct(handled, b.volume),
        pctAbandoned: pct(b.abandoned, b.volume),
        pctTransferred: pct(b.transferred, b.volume),
      };
    })
    .sort((a, b) => b.volume - a.volume);
}

function safeAvg(sum, count) {
  return count > 0 ? Math.round(sum / count) : null;
}
function pct(n, total) {
  return total > 0 ? Math.round((n / total) * 100) : 0;
}
function isTransfer(reason) {
  if (!reason) return false;
  const r = reason.toLowerCase();
  return r.includes('transfer') || r.includes('consult');
}

function PctBadge({ pct, color }) {
  if (pct == null || Number.isNaN(pct)) return <Text size="xs" c="dimmed">—</Text>;
  const variant = pct >= 20 ? 'filled' : 'light';
  return <Badge size="sm" color={color} variant={variant}>{pct}%</Badge>;
}
